import { useAuth } from '../../../contexts/AuthContext';
import { LoadingState } from '../../ui/loading-state';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogHeader, 
  DialogTitle,
  DialogFooter
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { 
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '../../ui/form';
import { Input } from '../../ui/input';
import { useState } from 'react';
import { useUpdateUser } from '../../../lib/supabase/users/supabaseUserService';
import { toast } from 'react-hot-toast';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

interface NameGuardProps {
  children: React.ReactNode;
}

// Define the form schema
const nameSchema = z.object({
  full_name: z.string()
    .min(1, "Name is required")
    .refine(value => /^[a-zA-Z\s]+$/.test(value), {
      message: "Name can only contain alphabetic characters and spaces",
    })
    .refine(value => value.trim().length >= 2, {
      message: "Name must be at least 2 characters long",
    }),
});

type NameFormValues = z.infer<typeof nameSchema>;

const NameGuard = ({ children }: NameGuardProps): JSX.Element => {
  const { userData, loading: userLoading, refetchUserData } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const updateUser = useUpdateUser();

  const form = useForm<NameFormValues>({
    resolver: zodResolver(nameSchema),
    defaultValues: {
      full_name: '',
    },
    mode: "onChange",
    delayError: 500,
  });

  // Name validation function
  const isValidName = (name: string): boolean => {
    // Only allow alphabetic characters and spaces
    return /^[a-zA-Z\s]+$/.test(name);
  };

  if (userLoading || !userData) {
    return <LoadingState />;
  }

  const hasValidName = isValidName(userData.full_name);

  const onSubmit = async (data: NameFormValues) => {
    setIsSubmitting(true);

    try {
      await updateUser.mutateAsync({
        id: userData.id,
        user: { full_name: data.full_name.trim() }
      });
      
      // Refetch user data to update the UI
      await refetchUserData();
      
      toast.success('Name updated successfully');
      form.reset();
    } catch (err: any) {
      console.error('Error updating name:', err);
      toast.error(err.message || 'Failed to update name. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // If user hasn't valid name, show dialog
  if (!hasValidName) {
    return (
      <>
        <Dialog open={true}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-brand-dark dark:text-foreground flex items-center gap-2">
                Update Your Name
              </DialogTitle>
              <DialogDescription className="text-muted-foreground dark:text-muted-foreground">
                Your current name "<span className="font-medium text-foreground">{userData.full_name}</span>" contains invalid characters. 
                Please update it to use only alphabetic characters and spaces. This ensures compatibility with our payment processing system.
              </DialogDescription>
            </DialogHeader>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="full_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-muted-foreground dark:text-muted-foreground">
                          Full Name
                        </FormLabel>
                        <FormControl>
                          <Input 
                            {...field}
                            placeholder="Enter your name using alphabetic characters only"
                            disabled={isSubmitting}
                            onBlur={() => {
                              field.onBlur();
                              form.trigger('full_name');
                            }}
                            onChange={(e) => {
                              field.onChange(e);
                              form.trigger('full_name');
                            }}
                          />
                        </FormControl>
                        <FormMessage className="text-sm text-brand-red" />
                      </FormItem>
                    )}
                  />
                </div>

                <DialogFooter>
                  <div className="flex flex-col gap-2 sm:flex-row sm:justify-end w-full">
                    <Button 
                      type="submit"
                      disabled={isSubmitting || !form.formState.isValid}
                      className="w-full sm:w-auto"
                    >
                      {isSubmitting ? 'Updating...' : 'Update Name'}
                    </Button>
                  </div>
                </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return <>{children}</>;
};

export default NameGuard; 